import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MessageSquare,
  Users,
  Send,
  Bot,
  CheckCircle,
  XCircle,
  Clock,
  Headphones,
  Target,
  AlertCircle,
  BarChart3,
  Mail,
  UserCheck,
  MessageCircle,
  Timer,
  SmilePlus,
  Meh,
  Frown,
  Award,
  AlertTriangle,
  Ban,
} from "lucide-react";
import { WhatsAppOutlined, InstagramFilled, FacebookFilled } from '@ant-design/icons';
import Layout from "../components/Layout";
import MetricCard from "../components/MetricCard";
import OnboardingWizard from "../components/OnboardingWizard";
import LineChart from "../components/charts/LineChart";
import DonutChart from "../components/charts/DonutChart";
import { instancesAPI } from "../services/api";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { formatarDataHora } from "../utils/dateFormatter";

import { planosAPI } from "../services/planosService";
import { cadastroAPI, emailAPI, faturamentoAPI } from "../services/api";
import DashboardSkeleton from '../components/skeletons/DashboardSkeleton';

const Dashboard = () => {
  const { darkMode } = useTheme();
  const [stats, setStats] = useState({ loading: true });
  const [recentMessages, setRecentMessages] = useState([]);
  const [chartData, setChartData] = useState({});
  const [trialInfo, setTrialInfo] = useState(null);
  const [cadastroIncompleto, setCadastroIncompleto] = useState(null);
  const [emailNaoConfirmado, setEmailNaoConfirmado] = useState(false);
  const [reenviandoEmail, setReenviandoEmail] = useState(false);
  const [showCadastroModal, setShowCadastroModal] = useState(false);
  const [cadastroForm, setCadastroForm] = useState({ cpf_cnpj: '', endereco: '', cidade: '', estado: '', cep: '' });
  const [salvandoCadastro, setSalvandoCadastro] = useState(false);
  const [ciclo, setCiclo] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

  let isAdmin = () => false;
  let isGerente = () => false;
  try {
    const auth = useAuth();
    isAdmin = auth.isAdmin;
    isGerente = auth.isGerente;
  } catch {}
  const isGestor = isAdmin() || isGerente();

  useEffect(() => { loadDashboardData(); loadTrialInfo(); loadCadastroStatus(); loadCiclo(); }, []);

  const loadCiclo = async () => {
    try {
      const res = await faturamentoAPI.getCicloAtual(user.cliente_id);
      setCiclo(res.data);
    } catch {}
  };

  const loadCadastroStatus = async () => {
    try {
      const res = await cadastroAPI.verificar(user.cliente_id);
      if (res.data.camposFaltando?.includes('email_confirmado')) setEmailNaoConfirmado(true);
      const outrosCampos = (res.data.camposFaltando || []).filter(c => c !== 'email_confirmado');
      if (outrosCampos.length > 0) setCadastroIncompleto(outrosCampos);
    } catch {}
  };

  const reenviarEmail = async () => {
    setReenviandoEmail(true);
    try {
      await emailAPI.resendConfirmation();
      toast.success('Email de confirmação reenviado!');
    } catch { toast.error('Erro ao reenviar email'); }
    finally { setReenviandoEmail(false); }
  };

  const salvarCadastro = async () => {
    if (!cadastroForm.endereco || !cadastroForm.cidade || !cadastroForm.estado || !cadastroForm.cep) return;
    setSalvandoCadastro(true);
    try {
      const data = { ...cadastroForm };
      if (data.cpf_cnpj) data.cnpj = data.cpf_cnpj;
      delete data.cpf_cnpj;
      await cadastroAPI.completar(user.cliente_id, data);
      setCadastroIncompleto(null);
      setShowCadastroModal(false);
    } catch {} finally { setSalvandoCadastro(false); }
  };

  const loadTrialInfo = async () => {
    try {
      const res = await planosAPI.getUso(user.cliente_id);
      const plano = res.data?.plano_atual;
      if (plano && parseFloat(plano.preco) === 0) {
        const inicio = new Date(res.data?.data_inicio_ciclo || Date.now());
        const expira = new Date(inicio.getTime() + 7 * 24 * 60 * 60 * 1000);
        const diasRestantes = Math.max(0, Math.ceil((expira - Date.now()) / (24 * 60 * 60 * 1000)));
        setTrialInfo({ diasRestantes, expira });
      }
    } catch {}
  };

  const loadDashboardData = async () => {
    try {
      const [instanceRes, metricsRes, chartsRes, recentRes] = await Promise.allSettled([
        instancesAPI.list(user.cliente_id),
        fetch(`${apiUrl}/dashboard/metrics/${user.cliente_id}`).then(r => r.ok ? r.json() : {}),
        fetch(`${apiUrl}/dashboard/charts/${user.cliente_id}`).then(r => r.ok ? r.json() : {}),
        fetch(`${apiUrl}/dashboard/recent/${user.cliente_id}`).then(r => r.ok ? r.json() : []),
      ]);
      const instancias = instanceRes.status === "fulfilled" ? (instanceRes.value?.data || []) : [];
      const canaisAtivos = instancias.filter(i => i.status === 'conectada').length;
      const canaisTotal = instancias.length;
      setStats({ ...(metricsRes.value || {}), canaisAtivos, canaisTotal, loading: false });
      setChartData(chartsRes.value || {});
      setRecentMessages(recentRes.value || []);
    } catch {
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const formatTempo = (seg) => {
    if (!seg) return "—";
    if (seg < 60) return `${seg}s`;
    if (seg < 3600) return `${Math.round(seg / 60)}min`;
    return `${(seg / 3600).toFixed(1)}h`;
  };

  if (stats.loading) return (
    <Layout><DashboardSkeleton /></Layout>
  );

  const c = darkMode ? "bg-zinc-800 border-gray-700" : "bg-white border-gray-200";
  const t1 = darkMode ? "text-white" : "text-gray-900";
  const t2 = darkMode ? "text-gray-400" : "text-gray-500";
  const bg2 = darkMode ? "bg-zinc-700" : "bg-gray-50";

  const sla = stats.sla || {};
  const sentimento = stats.sentimento || {};
  const totalSent = sentimento.positivo + sentimento.neutro + sentimento.negativo;
  const canais = stats.canais || [];
  const canalIcons = { whatsapp: WhatsAppOutlined, instagram: InstagramFilled, facebook: FacebookFilled };
  const canalColors = { whatsapp: "#25D366", instagram: "#E1306C", facebook: "#1877F2" };
  const horarios = stats.horarios || [];
  const maxHora = Math.max(...horarios.map(h => h.total), 1);
  const picoHora = horarios.reduce((a, b) => b.total > a.total ? b : a, { hora: 0, total: 0 });

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-6">
        {/* ── ALERTA DE FATURA EM ABERTO ── */}
        {ciclo?.suspenso ? (
          <Link to="/financeiro" className="block rounded-2xl p-5 bg-gradient-to-r from-red-600 to-red-700 text-white hover:opacity-95 transition">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Ban className="h-6 w-6 shrink-0" />
                <div>
                  <p className="font-bold text-lg">Conta suspensa por inadimplência</p>
                  <p className="text-red-100 text-sm">Regularize a fatura para reativar os envios e campanhas.</p>
                </div>
              </div>
              <span className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-red-600 shrink-0">Pagar agora</span>
            </div>
          </Link>
        ) : ciclo?.inadimplente ? (
          <Link to="/financeiro" className="block rounded-2xl p-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-95 transition">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 shrink-0" />
                <div>
                  <p className="font-bold text-lg">Fatura vencida — pague para evitar a suspensão</p>
                  <p className="text-amber-100 text-sm">Sua conta será suspensa em breve se o pagamento não for identificado.</p>
                </div>
              </div>
              <span className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-amber-600 shrink-0">Pagar agora</span>
            </div>
          </Link>
        ) : ciclo?.fatura_pendente ? (
          <Link to="/financeiro" className="block rounded-2xl p-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-95 transition">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 shrink-0" />
                <div>
                  <p className="font-bold text-lg">Você tem uma fatura em aberto</p>
                  <p className="text-blue-100 text-sm">
                    {ciclo.plano_nome ? `Plano ${ciclo.plano_nome} — ` : ''}
                    R$ {Number(ciclo.fatura_pendente.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}. Pague via boleto ou PIX.
                  </p>
                </div>
              </div>
              <span className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-blue-600 shrink-0">Pagar agora</span>
            </div>
          </Link>
        ) : null}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${t1}`}>Dashboard</h1>
            <p className={`mt-1 text-sm ${t2}`}>Bem-vindo, {user.nome}!</p>
          </div>
          <div className={`flex items-center px-3 py-1.5 rounded-lg text-sm ${
            stats.canaisAtivos === stats.canaisTotal && stats.canaisTotal > 0
              ? darkMode ? "text-green-400 bg-green-900/20" : "text-green-600 bg-green-100"
              : stats.canaisAtivos > 0
              ? darkMode ? "text-yellow-400 bg-yellow-900/20" : "text-yellow-600 bg-yellow-100"
              : darkMode ? "text-red-400 bg-red-900/20" : "text-red-600 bg-red-100"
          }`}>
            {stats.canaisAtivos === stats.canaisTotal && stats.canaisTotal > 0
              ? <CheckCircle className="h-4 w-4" />
              : stats.canaisAtivos > 0 ? <Clock className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            <span className="ml-1.5 font-medium">{stats.canaisAtivos}/{stats.canaisTotal} canais ativos</span>
          </div>
        </div>

        {/* Onboarding */}
        <OnboardingWizard clienteId={user.cliente_id} />

        {/* Banner Trial */}
        {trialInfo && (
          <Link to="/planos" className={`block rounded-2xl p-4 transition-all hover:scale-[1.01] ${
            trialInfo.diasRestantes <= 2
              ? 'bg-gradient-to-r from-red-600 to-red-700 text-white'
              : trialInfo.diasRestantes <= 4
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
          }`}>
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-bold">
                    {trialInfo.diasRestantes === 0 ? 'Seu trial expira hoje!' : `${trialInfo.diasRestantes} dia${trialInfo.diasRestantes !== 1 ? 's' : ''} restante${trialInfo.diasRestantes !== 1 ? 's' : ''} no trial`}
                  </p>
                  <p className="text-sm opacity-90">Escolha um plano para continuar usando a plataforma</p>
                </div>
              </div>
              <span className="px-4 py-1.5 rounded-xl text-sm font-bold bg-white/20 hover:bg-white/30 shrink-0">Ver planos →</span>
            </div>
          </Link>
        )}

        {/* Banner Email não confirmado */}
        {emailNaoConfirmado && (
          <div className={`rounded-2xl p-4 ${darkMode ? 'bg-blue-900/30 border border-blue-700/50' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-500 shrink-0" />
                <div className="min-w-0">
                  <p className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>Confirme seu email</p>
                  <p className={`text-sm ${darkMode ? 'text-blue-400/70' : 'text-blue-600'}`}>Verifique sua caixa de entrada e clique no link de confirmação</p>
                </div>
              </div>
              <button onClick={reenviarEmail} disabled={reenviandoEmail}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 ${darkMode ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} disabled:opacity-50`}>
                {reenviandoEmail ? 'Enviando...' : 'Reenviar email'}
              </button>
            </div>
          </div>
        )}

        {/* Banner Cadastro Incompleto */}
        {cadastroIncompleto && (
          <button onClick={() => setShowCadastroModal(true)} className={`w-full rounded-2xl p-4 text-left transition-all hover:scale-[1.005] ${darkMode ? 'bg-amber-900/30 border border-amber-700/50' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <p className={`font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>Complete seu cadastro</p>
                  <p className={`text-sm ${darkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>Preencha seu endereço para conectar canais e assinar um plano</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-lg text-xs font-medium ${darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>Completar →</span>
            </div>
          </button>
        )}

        {/* Modal Completar Cadastro */}
        {showCadastroModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowCadastroModal(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className={`relative w-full max-w-md rounded-2xl p-6 ${darkMode ? 'bg-zinc-900' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
              <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Complete seu cadastro</h3>
              <div className="space-y-3">
                {cadastroIncompleto?.includes('cpf_cnpj') && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>CPF ou CNPJ *</label>
                  <input type="text" placeholder="000.000.000-00" value={cadastroForm.cpf_cnpj} onChange={e => setCadastroForm({...cadastroForm, cpf_cnpj: e.target.value.replace(/\D/g,'')})}
                    className={`w-full rounded-lg py-2 px-3 text-sm ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border`} />
                </div>
                )}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Endereço *</label>
                  <input type="text" placeholder="Rua das Flores, 123" value={cadastroForm.endereco} onChange={e => setCadastroForm({...cadastroForm, endereco: e.target.value})}
                    className={`w-full rounded-lg py-2 px-3 text-sm ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border`} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>CEP *</label>
                    <input type="text" placeholder="01234567" maxLength={8} value={cadastroForm.cep} onChange={e => setCadastroForm({...cadastroForm, cep: e.target.value.replace(/\D/g,'')})}
                      className={`w-full rounded-lg py-2 px-3 text-sm ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cidade *</label>
                    <input type="text" value={cadastroForm.cidade} onChange={e => setCadastroForm({...cadastroForm, cidade: e.target.value})}
                      className={`w-full rounded-lg py-2 px-3 text-sm ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>UF *</label>
                    <input type="text" placeholder="SP" maxLength={2} value={cadastroForm.estado} onChange={e => setCadastroForm({...cadastroForm, estado: e.target.value.toUpperCase()})}
                      className={`w-full rounded-lg py-2 px-3 text-sm ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border`} />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setShowCadastroModal(false)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'}`}>Cancelar</button>
                <button onClick={salvarCadastro} disabled={salvandoCadastro || !cadastroForm.endereco || !cadastroForm.cep || !cadastroForm.cidade || !cadastroForm.estado}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-50">
                  {salvandoCadastro ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto" /> : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alerta SLA */}
        {(sla.mais4h > 0 || sla.de1ha4h > 0) && (
          <div className={`rounded-lg border p-4 ${sla.mais4h > 0
            ? darkMode ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
            : darkMode ? "bg-yellow-900/20 border-yellow-800" : "bg-yellow-50 border-yellow-200"
          }`}>
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className={`h-5 w-5 ${sla.mais4h > 0
                  ? darkMode ? "text-red-400" : "text-red-600"
                  : darkMode ? "text-yellow-400" : "text-yellow-600"
                }`} />
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${t1}`}>
                    {stats.conversasPendentes} conversa{stats.conversasPendentes !== 1 ? 's' : ''} sem resposta
                  </p>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {sla.mais4h > 0 && <span className="text-xs text-red-500 font-medium">🔴 {sla.mais4h} há +4h</span>}
                    {sla.de1ha4h > 0 && <span className="text-xs text-orange-500 font-medium">🟠 {sla.de1ha4h} de 1-4h</span>}
                    {sla.de15a60 > 0 && <span className="text-xs text-yellow-500 font-medium">🟡 {sla.de15a60} de 15-60min</span>}
                    {sla.ate15min > 0 && <span className="text-xs text-green-500 font-medium">🟢 {sla.ate15min} &lt;15min</span>}
                  </div>
                </div>
              </div>
              <Link to="/conversas" className={`text-sm font-medium px-3 py-1.5 rounded ${
                darkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-white text-gray-700 hover:bg-gray-100"
              }`}>Atender</Link>
            </div>
          </div>
        )}

        {/* Métricas de Atendimento */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
          <MetricCard title="Conversas Hoje" value={stats.conversasHoje || 0} icon={MessageCircle} color="blue" darkMode={darkMode} tooltip="Total de conversas recebidas hoje (desde meia-noite)" />
          <MetricCard title="Sem Resposta" value={stats.conversasPendentes || 0} icon={AlertCircle} color={stats.conversasPendentes > 5 ? "red" : "orange"} darkMode={darkMode} tooltip="Conversas que ainda não foram respondidas por nenhum atendente ou IA" />
          <MetricCard title="Tempo Médio" value={stats.tempoMedioResposta || 0} icon={Timer} color="purple" format="time" darkMode={darkMode} tooltip="Tempo médio entre o recebimento e a primeira resposta humana (este mês)" />
          <MetricCard title="Resolução 1º Contato" value={stats.taxaFCR || 0} icon={Award} color="green" format="percentage" darkMode={darkMode} tooltip="% de conversas resolvidas sem o cliente retornar em 24h" />
        </div>

        {/* Métricas Gerais */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
          <MetricCard title="Taxa de Entrega" value={stats.taxaEntrega || 0} icon={CheckCircle} color="green" format="percentage" darkMode={darkMode} tooltip="% de mensagens enviadas que foram entregues ao destinatário" />
          <MetricCard title="Taxa de Leitura" value={stats.taxaLeitura || 0} icon={Target} color="blue" format="percentage" darkMode={darkMode} tooltip="% de mensagens entregues que foram lidas pelo destinatário" />
          <MetricCard title="Contatos" value={stats.totalContatos || 0} icon={Users} color="indigo" darkMode={darkMode} tooltip="Total de contatos ativos cadastrados na plataforma" />
          <MetricCard title="Campanhas Ativas" value={stats.campanhasAtivas || 0} icon={Send} color="purple" darkMode={darkMode} tooltip="Campanhas ativas no momento" />
        </div>

        {/* Sentimento + Canais + FCR */}
        <div className="grid grid-cols-1 gap-2 sm:gap-4 lg:grid-cols-3">
          {/* Sentimento */}
          <div className={`rounded-lg shadow-sm border p-3 sm:p-5 ${c}`}>
            <h4 className={`text-sm font-semibold mb-3 ${t1}`}>Sentimento das Conversas</h4>
            {totalSent > 0 ? (
              <>
                <div className="flex gap-1 h-3 rounded-full overflow-hidden mb-3">
                  {sentimento.positivo > 0 && <div className="bg-green-500" style={{ width: `${(sentimento.positivo / totalSent) * 100}%` }} />}
                  {sentimento.neutro > 0 && <div className="bg-gray-400" style={{ width: `${(sentimento.neutro / totalSent) * 100}%` }} />}
                  {sentimento.negativo > 0 && <div className="bg-red-500" style={{ width: `${(sentimento.negativo / totalSent) * 100}%` }} />}
                </div>
                <div className={`flex justify-between text-xs ${t2}`}>
                  <span className="flex items-center gap-1"><SmilePlus className="h-3.5 w-3.5 text-green-500" /> {sentimento.positivo} ({Math.round((sentimento.positivo / totalSent) * 100)}%)</span>
                  <span className="flex items-center gap-1"><Meh className="h-3.5 w-3.5 text-gray-400" /> {sentimento.neutro} ({Math.round((sentimento.neutro / totalSent) * 100)}%)</span>
                  <span className="flex items-center gap-1"><Frown className="h-3.5 w-3.5 text-red-500" /> {sentimento.negativo} ({Math.round((sentimento.negativo / totalSent) * 100)}%)</span>
                </div>
              </>
            ) : <p className={`text-sm ${t2}`}>Sem dados de sentimento</p>}
          </div>

          {/* Canais */}
          <div className={`rounded-lg shadow-sm border p-3 sm:p-5 ${c}`}>
            <h4 className={`text-sm font-semibold mb-3 ${t1}`}>Conversas por Canal</h4>
            <div className="space-y-3">
              {canais.map(ch => {
                const Icon = canalIcons[ch.canal] || MessageSquare;
                const totalCanais = canais.reduce((s, x) => s + x.total, 0);
                const pct = totalCanais > 0 ? Math.round((ch.total / totalCanais) * 100) : 0;
                const isAntIcon = ['whatsapp','instagram','facebook'].includes(ch.canal);
                return (
                  <div key={ch.canal} className="flex items-center gap-3">
                    {isAntIcon
                      ? <Icon style={{ color: canalColors[ch.canal], fontSize: 16 }} />
                      : <Icon className="h-4 w-4 flex-shrink-0" style={{ color: canalColors[ch.canal] || '#888' }} />
                    }
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={`capitalize ${t1}`}>{ch.canal}</span>
                        <span className={t2}>{ch.total} ({pct}%)</span>
                      </div>
                      <div className={`h-2 rounded-full ${darkMode ? 'bg-zinc-600' : 'bg-gray-200'}`}>
                        <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: canalColors[ch.canal] || '#888' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {canais.length === 0 && <p className={`text-sm ${t2}`}>Sem dados</p>}
            </div>
          </div>

          {/* Horário de Pico */}
          <div className={`rounded-lg shadow-sm border p-3 sm:p-5 ${c}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-semibold ${t1}`}>Horário de Pico</h4>
              {picoHora.total > 0 && <span className={`text-xs ${t2}`}>Pico: {picoHora.hora}h ({picoHora.total})</span>}
            </div>
            <div className="flex items-end gap-[2px]" style={{ height: 64 }}>
              {horarios.map(h => (
                <div key={h.hora} className="flex-1 flex flex-col justify-end group relative" style={{ height: 64 }}>
                  <div
                    className={`w-full rounded-t-sm transition-colors ${
                      h.hora === picoHora.hora ? 'bg-orange-500' :
                      h.total > maxHora * 0.6 ? (darkMode ? 'bg-blue-400' : 'bg-blue-500') :
                      h.total > 0 ? (darkMode ? 'bg-blue-600' : 'bg-blue-300') :
                      (darkMode ? 'bg-zinc-600' : 'bg-gray-200')
                    }`}
                    style={{ height: Math.max(Math.round((h.total / maxHora) * 60), 3) }}
                    title={`${h.hora}h: ${h.total} conversas`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className={`text-[10px] ${t2}`}>0h</span>
              <span className={`text-[10px] ${t2}`}>6h</span>
              <span className={`text-[10px] ${t2}`}>12h</span>
              <span className={`text-[10px] ${t2}`}>18h</span>
              <span className={`text-[10px] ${t2}`}>23h</span>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 gap-3 sm:gap-6 lg:grid-cols-2">
          <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${c}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-sm font-semibold ${t1}`}>Conversas por Dia</h4>
              <span className={`text-xs ${t2}`}>7 dias</span>
            </div>
            <LineChart data={chartData.messagesChart || []} title="" color="#3B82F6" darkMode={darkMode} />
          </div>
          <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${c}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-sm font-semibold ${t1}`}>Tempo de Resposta</h4>
              <span className={`text-xs ${t2}`}>Média em seg · 7 dias</span>
            </div>
            <LineChart data={chartData.responseTimeChart || []} title="" color="#F59E0B" darkMode={darkMode} />
          </div>
        </div>

        {/* Distribuição + Conversas Recentes */}
        <div className="grid grid-cols-1 gap-3 sm:gap-6 lg:grid-cols-2">
          <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${c}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-sm font-semibold ${t1}`}>Quem Responde</h4>
              <span className={`text-xs ${t2}`}>Este mês</span>
            </div>
            <DonutChart title="" data={chartData.distributionData || [
              { label: "IA", value: stats.respostasIA || 0, color: "#10B981" },
              { label: "Humano", value: stats.respostasHumano || 0, color: "#3B82F6" },
              { label: "Pendente", value: stats.semResposta || 0, color: "#F59E0B" },
            ]} darkMode={darkMode} />
            <div className="flex justify-center gap-6 mt-4">
              <span className={`flex items-center gap-1.5 text-xs ${t2}`}><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> IA ({stats.respostasIA || 0})</span>
              <span className={`flex items-center gap-1.5 text-xs ${t2}`}><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Humano ({stats.respostasHumano || 0})</span>
              <span className={`flex items-center gap-1.5 text-xs ${t2}`}><span className="w-2.5 h-2.5 rounded-full bg-yellow-500" /> Pendente ({stats.semResposta || 0})</span>
            </div>
          </div>

          {/* Conversas Recentes */}
          <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${c}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-sm font-semibold ${t1}`}>Conversas Recentes</h4>
              <Link to="/conversas" className={`text-xs font-medium ${darkMode ? "text-blue-400" : "text-blue-600"}`}>Ver todas →</Link>
            </div>
            {recentMessages.length > 0 ? (
              <div className="space-y-2">
                {recentMessages.map((msg, i) => (
                  <Link key={i}
                    to={`/conversas?telefone=${encodeURIComponent(msg.telefone)}${msg.instancia_id ? `&instancia=${msg.instancia_id}` : ''}`}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${bg2} ${darkMode ? "hover:bg-zinc-600" : "hover:bg-gray-100"}`}>
                    <div className="flex-shrink-0 relative">
                      {msg.foto ? (
                        <img src={msg.foto} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${darkMode ? "bg-gray-600 text-gray-200" : "bg-gray-200 text-gray-600"}`}>
                          {(msg.nome || msg.telefone || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      {msg.pendentes > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{msg.pendentes}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium truncate ${t1}`}>{msg.nome || msg.telefone}</p>
                        <span className={`text-[10px] flex-shrink-0 ml-2 ${t2}`}>{formatarDataHora(msg.create_at)}</span>
                      </div>
                      <p className={`text-xs truncate ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{msg.text}</p>
                    </div>
                    <div className="flex-shrink-0">
                      {msg.pendentes > 0 ? <AlertCircle className="h-4 w-4 text-yellow-500" /> :
                       msg.respondida_por_ia ? <Bot className="h-4 w-4 text-green-500" /> :
                       <UserCheck className="h-4 w-4 text-blue-500" />}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className={`mx-auto h-10 w-10 ${t2}`} />
                <p className={`mt-2 text-sm ${t2}`}>Nenhuma conversa recente</p>
              </div>
            )}
          </div>
        </div>

        {/* Performance por Atendente (só gestor) */}
        {isGestor && stats.atendentes?.length > 0 && (
          <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${c}`}>
            <h4 className={`text-sm font-semibold mb-4 ${t1}`}>Performance da Equipe</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <th className={`text-left py-2 font-medium ${t2}`}>Atendente</th>
                    <th className={`text-center py-2 font-medium ${t2}`}>Respondidas</th>
                    <th className={`text-center py-2 font-medium ${t2}`}>Tempo Médio</th>
                    <th className={`text-center py-2 font-medium ${t2}`}>Atendendo</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.atendentes.map(a => (
                    <tr key={a.id} className={`border-b ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                      <td className={`py-2.5 ${t1}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${a.online ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <span>{a.nome}</span>
                          {a.online && <span className={`text-[10px] ${darkMode ? 'text-green-400' : 'text-green-600'}`}>online</span>}
                        </div>
                      </td>
                      <td className={`text-center py-2.5 ${t1}`}>{a.respondidas}</td>
                      <td className={`text-center py-2.5 ${t1}`}>{formatTempo(a.tempoMedio)}</td>
                      <td className="text-center py-2.5">
                        {a.sessoesAtivas > 0 ? (
                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
                            {a.sessoesAtivas} ativo{a.sessoesAtivas > 1 ? 's' : ''}
                          </span>
                        ) : <span className={`text-xs ${t2}`}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ações Rápidas */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
          <Link to="/conversas" className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow hover:shadow-md transition-all hover:scale-[1.02]">
            <Headphones className="h-6 w-6 mb-2" />
            <h3 className="text-sm font-medium">Atendimento</h3>
            <p className="text-xs opacity-80">Responder conversas</p>
          </Link>
          <Link to="/marketing/campanhas" className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow hover:shadow-md transition-all hover:scale-[1.02]">
            <Send className="h-6 w-6 mb-2" />
            <h3 className="text-sm font-medium">Campanhas</h3>
            <p className="text-xs opacity-80">Criar campanha</p>
          </Link>
          <Link to="/contatos" className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow hover:shadow-md transition-all hover:scale-[1.02]">
            <Users className="h-6 w-6 mb-2" />
            <h3 className="text-sm font-medium">Contatos</h3>
            <p className="text-xs opacity-80">Gerenciar contatos</p>
          </Link>
          <Link to="/atendimento/agente-ia" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow hover:shadow-md transition-all hover:scale-[1.02]">
            <Bot className="h-6 w-6 mb-2" />
            <h3 className="text-sm font-medium">Agente de IA</h3>
            <p className="text-xs opacity-80">Configurar assistente</p>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
